import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { ConnectivityService } from '../../core/connectivity/connectivity.service';
import { describeDropReason, OfflineQueueService } from '../../core/offline/offline-queue.service';
import { QUEUED_OP_KIND_LABEL } from '../../core/offline/queued-op-labels';

/**
 * A persistent strip that appears whenever the device loses its connection
 * (prod-readiness 4.3). Mounted once in the shell, above the content.
 *
 * The honest message: reads keep working (Firestore serves them from the
 * on-disk cache), simple writes are held and sent on reconnect, but anything
 * that goes through a Cloud Function — taking payment, running payroll,
 * settling a bill — cannot happen until the connection is back. Controls
 * that call one disable themselves while `ConnectivityService.offline()` is
 * true; this banner is the venue-wide explanation for why.
 */
@Component({
  selector: 'nv-offline-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (connectivity.offline()) {
      <div class="nv-offline" role="status" aria-live="polite">
        <span class="nv-offline__dot" aria-hidden="true"></span>
        <span class="nv-offline__text">
          <strong>You're offline.</strong>
          Open orders and the menu still work. New orders and edits are saved and
          will send when you're back. Payments, billing and payroll are paused
          until then.
          @if (pending() > 0) {
            <strong>{{ pending() }} waiting to send.</strong>
          }
        </span>
      </div>
    } @else if (pending() > 0) {
      <div class="nv-offline nv-offline--syncing" role="status" aria-live="polite">
        <span class="nv-offline__dot" aria-hidden="true"></span>
        <span class="nv-offline__text">
          Back online — sending {{ pending() }} saved {{ pending() === 1 ? 'order' : 'orders' }}…
        </span>
      </div>
    }
    @if (dropped().length > 0) {
      <div class="nv-offline nv-offline--dropped" role="status" aria-live="polite">
        <span class="nv-offline__dot nv-offline__dot--danger" aria-hidden="true"></span>
        <span class="nv-offline__text">
          <button type="button" class="nv-offline__droplink" (click)="expanded.set(!expanded())">
            {{ dropped().length }} {{ dropped().length === 1 ? 'item' : 'items' }} couldn't be sent
            {{ expanded() ? '▲' : '▼' }}
          </button>
          @if (expanded()) {
            <ul class="nv-offline__droplist">
              @for (entry of dropped(); track entry.op.seq) {
                <li>
                  <span>{{ kindLabel(entry.op.kind) }} — {{ reasonFor(entry.error) }}</span>
                  <button type="button" (click)="dismiss(entry.op.seq)">Dismiss</button>
                </li>
              }
            </ul>
          }
        </span>
      </div>
    }
  `,
  styles: [
    `
      .nv-offline {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        background: var(--nvc-warning-wash);
        color: var(--nvc-warning);
        border-bottom: 1px solid var(--nvc-warning);
        font-family: var(--nvc-font);
        font-size: 0.8125rem;
        line-height: 1.35;
      }
      .nv-offline__dot {
        flex: none;
        width: 0.5rem;
        height: 0.5rem;
        margin-top: 0.3rem;
        border-radius: 50%;
        background: currentColor;
        animation: nv-offline-pulse 1.6s ease-in-out infinite;
      }
      .nv-offline__text strong {
        font-weight: 600;
      }
      .nv-offline--syncing {
        background: var(--nvc-surface-subtle);
        color: var(--nvc-text-muted);
        border-bottom-color: var(--nvc-line);
      }
      @keyframes nv-offline-pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.3;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .nv-offline__dot {
          animation: none;
        }
      }
      .nv-offline--dropped {
        background: var(--nvc-danger-wash, #fdecea);
        color: var(--nvc-danger, #b3261e);
        border-bottom-color: var(--nvc-danger, #b3261e);
      }
      .nv-offline__dot--danger {
        animation: none;
      }
      .nv-offline__droplink {
        background: none;
        border: none;
        padding: 0;
        font: inherit;
        color: inherit;
        font-weight: 600;
        cursor: pointer;
      }
      .nv-offline__droplist {
        list-style: none;
        margin: 0.35rem 0 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }
      .nv-offline__droplist li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
      }
      .nv-offline__droplist button {
        background: none;
        border: 1px solid currentColor;
        border-radius: 4px;
        padding: 0.1rem 0.5rem;
        color: inherit;
        cursor: pointer;
        font: inherit;
        font-size: 0.75rem;
      }
    `,
  ],
})
export class NvOfflineBanner {
  protected readonly connectivity = inject(ConnectivityService);
  private readonly queue = inject(OfflineQueueService);
  protected readonly pending = computed(() => this.queue.pendingCount());
  protected readonly dropped = computed(() => this.queue.dropped());
  /** Collapsed by default — the count alone is enough for the common case; expanding is an explicit ask. */
  protected readonly expanded = signal(false);

  protected kindLabel(kind: keyof typeof QUEUED_OP_KIND_LABEL): string {
    return QUEUED_OP_KIND_LABEL[kind];
  }

  protected reasonFor(code: string): string {
    return describeDropReason(code);
  }

  protected dismiss(seq: number): void {
    this.queue.dismissDropped(seq);
  }
}
